import React, { useState, useEffect } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useAuth } from '../hooks/useAuth';
import UserNavBar from '../components/UserNavBar';

// Reusing the password validation from AuthPage
const validatePassword = (password: string) => {
  if (password.length < 8) return 'Must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Must contain at least 1 uppercase letter';
  if (!/[a-z]/.test(password)) return 'Must contain at least 1 lowercase letter';
  if (!/[0-9]/.test(password)) return 'Must contain at least 1 number';
  return '';
};

interface FormErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

interface UserProfile {
  first_name: string;
  last_name: string;
  organization: string;
  email: string;
}

const ProfileSettings: React.FC = () => {
  const supabase = useSupabase();
  const { user, signOut } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('users')
          .select('first_name, last_name, organization, email')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching user profile:', error);
          return;
        }

        setUserProfile(data);
      } catch (error) {
        console.error('Unexpected error fetching user profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [user, supabase]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);
    setIsSubmitting(true);
    setErrors({});

    try {
      // Validate new password
      const passwordError = validatePassword(newPassword);
      if (passwordError) {
        setErrors(prev => ({ ...prev, newPassword: passwordError }));
        setIsSubmitting(false);
        return;
      }

      // Check if passwords match
      if (newPassword !== confirmPassword) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
        setIsSubmitting(false);
        return;
      }

      // First verify the current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword
      });

      if (signInError) {
        setErrors(prev => ({ ...prev, currentPassword: 'Current password is incorrect' }));
        setIsSubmitting(false);
        return;
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        throw updateError;
      }

      // Clear form and show success message
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSubmitSuccess('Your password has been successfully updated.');
    } catch (error) {
      console.error('Error changing password:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'An error occurred while changing your password'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F55D3E]"></div>
      </div>
    );
  }

  return (
    <>
      <UserNavBar user={user} onSignOut={signOut} hideDashboardLink={false} />
      <div className="pt-16 container mx-auto max-w-4xl py-8 px-4">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mt-8 mb-2">Profile Settings</h2>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Account Information</h3>
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#F55D3E]"></div>
              <span className="text-gray-500">Loading user information...</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Email</p>
                  <p className="text-gray-900">{userProfile?.email || user.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Organization</p>
                  <p className="text-gray-900">{userProfile?.organization || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">First Name</p>
                  <p className="text-gray-900">{userProfile?.first_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Last Name</p>
                  <p className="text-gray-900">{userProfile?.last_name || 'N/A'}</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 text-blue-700 rounded-md">
                <p className="text-sm">
                  Please contact <a href="mailto:support@timelyirb.com" className="underline font-medium">support@timelyirb.com</a> to change your first name, last name, organization, or email.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Change Password</h3>

          {submitError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {submitError}
            </div>
          )}

          {submitSuccess && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {submitSuccess}
            </div>
          )}

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                onFocus={() => setFocusedField('currentPassword')}
                onBlur={() => setFocusedField(null)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${
                  errors.currentPassword ? 'border-red-500' : ''
                }`}
                required
              />
              {errors.currentPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.currentPassword}</p>
              )}
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={() => setFocusedField('newPassword')}
                onBlur={() => setFocusedField(null)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${
                  errors.newPassword ? 'border-red-500' : ''
                }`}
                required
              />
              {focusedField === 'newPassword' && (
                <p className="mt-1 text-xs text-gray-500">
                  Password must be at least 8 characters long and contain at least 1 uppercase letter,
                  1 lowercase letter, and 1 number
                </p>
              )}
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.newPassword}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F55D3E] focus:border-transparent ${
                  errors.confirmPassword ? 'border-red-500' : ''
                }`}
                required
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`bg-[#F55D3E] text-white py-2 px-4 rounded-lg hover:bg-[#F9A594] transition-colors duration-200 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ProfileSettings;
